const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) || '/api/v1';

const ACCESS_KEY = 'access_token';
const REFRESH_KEY = 'refresh_token';

export interface ApiError {
  status: number;
  body: unknown;
}

export function getAccessToken(): string | null {
  try {
    return localStorage.getItem(ACCESS_KEY);
  } catch {
    return null;
  }
}

export function getRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

export function setTokens(access: string, refresh?: string): void {
  try {
    localStorage.setItem(ACCESS_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  } catch {
    void 0;
  }
}

export function clearTokens(): void {
  try {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  } catch {
    void 0;
  }
}

function buildUrl(path: string): string {
  if (/^https?:\/\//.test(path)) return path;
  if (path.startsWith('/')) return `${BASE_URL}${path}`;
  return `${BASE_URL}/${path}`;
}

function buildHeaders(init?: RequestInit): HeadersInit {
  const headers = new Headers(init?.headers || {});
  const isFormData = typeof FormData !== 'undefined' && init?.body instanceof FormData;
  if (!isFormData && !headers.has('Content-Type') && init?.body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  const access = getAccessToken();
  if (access && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${access}`);
  }
  return headers;
}

async function tryRefresh(): Promise<boolean> {
  const refresh = getRefreshToken();
  if (!refresh) return false;
  try {
    const res = await fetch(buildUrl('/auth/refresh/'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh }),
    });
    if (!res.ok) {
      clearTokens();
      return false;
    }
    const data = (await res.json()) as { access?: string; refresh?: string };
    if (!data.access) {
      clearTokens();
      return false;
    }
    setTokens(data.access, data.refresh);
    return true;
  } catch {
    clearTokens();
    return false;
  }
}

export async function api(path: string, options: RequestInit = {}): Promise<Response> {
  const url = buildUrl(path);
  const init: RequestInit = { ...options, headers: buildHeaders(options) };
  let res = await fetch(url, init);
  if (res.status === 401 && !path.includes('/auth/refresh') && !path.includes('/auth/login')) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      res = await fetch(url, { ...options, headers: buildHeaders(options) });
    }
  }
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.clone().json();
    } catch {
      try {
        body = await res.clone().text();
      } catch {
        body = null;
      }
    }
    const err: ApiError = { status: res.status, body };
    throw err;
  }
  return res;
}

function encodeBody(data: unknown): BodyInit | undefined {
  if (data === undefined || data === null) return undefined;
  if (typeof FormData !== 'undefined' && data instanceof FormData) return data;
  return JSON.stringify(data);
}

export async function get<T = unknown>(path: string): Promise<T> {
  const res = await api(path, { method: 'GET' });
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export async function post<T = unknown>(path: string, body?: unknown): Promise<T> {
  const res = await api(path, { method: 'POST', body: encodeBody(body) });
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export async function patch<T = unknown>(path: string, body?: unknown): Promise<T> {
  const res = await api(path, { method: 'PATCH', body: encodeBody(body) });
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export async function del<T = unknown>(path: string): Promise<T> {
  const res = await api(path, { method: 'DELETE' });
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
